import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { createColumnHelper } from '@tanstack/react-table';
import {
  AlertCircle,
  DotsVertical,
  Phone,
  Pencil,
  RotateAnticlockwiseDots,
} from '@saleshandy/icons';
import { Dropdown } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ImageIcon } from '@saleshandy/design-system';
import Icon from '../../../../../../../../shared/design-system/components/atoms/icon/icon';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import { formatPhoneNumber } from '../../../../../../helpers/utils/get-formated-numbers';
import { COUNTRY_ISO_FLAG_MAP } from '../../../purchase-phone-number-form/constants';
import Mobile from '../../../../../../../../shared/svg/mobile';
import { PhoneNumberSettingsIcon } from '../../../../../../../../shared/svg/phone-number-settings-icon';
import { CheckboxIcon } from '../../../../../../../../shared/svg/checkbox-icon';
import Switch, {
  Size as SwitchSize,
} from '../../../../../../../../shared/design-system/components/atoms/switch';
import { updatePhoneNumberSettings } from '../../../../../../helpers/dialer.api';
import toaster, { Theme } from '../../../../../../../../shared/toaster';
import {
  executeOnRequestStatus,
  getIsRequestPending,
  formatNoteTimestamp,
} from '../../../../../../../../shared/utils';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';
import Modal from '../../../../../../../../shared/design-system/ui/modal';
import Select from '../../../../../../../../shared/design-system/components/select';
import Input from '../../../../../../../../shared/design-system/components/input';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';

import {
  PhoneNumber,
  DialerPhoneNumberStatus,
} from '../../../../../../types/phone-numbers';
import {
  setPrimaryPhoneNumberRequest,
  assignPhoneNumberRequest,
  setPhoneNumberLabelRequest,
  deletePhoneNumberRequest,
  revokePhoneNumberReleaseRequest,
} from '../../../../../../extra-actions';
import { resetSetPrimaryPhoneNumberRequest } from '../../../../../../dialer-slice';
import { RootState } from '../../../../../../../../store/root-reducer';
import DeletePhoneNumberModal from '../delete-phone-number-modal';
import RevokeReleaseModal from '../revoke-release-modal';
import hasPermission from '../../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../../shared/utils/access-control/enums/permissions';
import CopyButton from '../../../../../../../../shared/components/copy-button';
import TruncatedTextWithAutoTooltip from '../../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import GlobalRestrictionModal from '../../../../../../../home/components/global-restriction-modal';

// Phone number setting codes
export const PHONE_NUMBER_SETTING_CODES = {
  CALL_RECORDING_ENABLED: 'call-recording-enabled',
  VOICEMAIL_ENABLED: 'voicemail-enabled',
} as const;

const PrimaryNumberConfirmModal: React.FC<{
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}> = ({ show, onClose, onConfirm, isSubmitting }) => {
  const title = (
    <div className="d-flex align-items-center gap-10">
      <AlertCircle width={24} height={24} color="#D97706" />
      <span className="line-height-1">Make this the Primary Number?</span>
    </div>
  );

  return (
    <Modal
      show={show}
      className="make-primary-number-modal"
      title={title}
      onHide={onClose}
      primaryBtnLabel="Yes"
      secondaryBtnLabel="Cancel"
      primaryBtnClickedHandler={onConfirm}
      secondaryBtnClickedHandler={onClose}
      primaryBtnDisabled={isSubmitting}
      secondaryBtnDisabled={isSubmitting}
    >
      <p>
        This number will become your default calling number when number rotation
        is disabled.
      </p>
      <p>
        If Local Presence is enabled and no matching local number is available,
        this primary number will be used to call the prospect.
      </p>
    </Modal>
  );
};

// Call Forwarding Cell Component
const CallForwardingCell: React.FC<{ row: PhoneNumber }> = ({ row }) => {
  const isEnabled = !!row.callForwarding;
  const history = useHistory();

  return (
    <div className="feature-badge-chip-wrapper">
      <OverlayTooltip
        text={
          isEnabled ? 'Call Forwarding Enabled' : 'Call Forwarding Disabled'
        }
        placement={Placement.Bottom}
      >
        <div
          className={`feature-badge-chip ${isEnabled ? 'enabled' : 'disabled'}`}
        >
          <span>{isEnabled ? 'Enabled' : 'Disabled'}</span>
        </div>
      </OverlayTooltip>

      <OverlayTooltip text="Configure">
        <div
          className="feature-settings-icon"
          {...accessibleOnClick(() => {
            history.push(
              `/dialer/phone-numbers/${row.id}/settings#call-forwarding`,
            );
          })}
        >
          <PhoneNumberSettingsIcon width={16} height={16} />
        </div>
      </OverlayTooltip>
    </div>
  );
};

// Call Recording Cell Component
const CallRecordingCell: React.FC<{ row: PhoneNumber }> = ({ row }) => {
  const [isEnabled, setIsEnabled] = useState(!!row.callRecording);
  const phoneNumberId = row.id;
  const history = useHistory();
  useEffect(() => {
    // keep UI in sync when fresh data arrives
    setIsEnabled(!!row.callRecording);
  }, [row.callRecording]);

  const handleToggle = async (checked: boolean) => {
    // Optimistic update
    setIsEnabled(checked);
    try {
      await updatePhoneNumberSettings({
        phoneNumberIds: [phoneNumberId],
        settings: [
          {
            code: PHONE_NUMBER_SETTING_CODES.CALL_RECORDING_ENABLED,
            value: checked ? 'true' : 'false',
          },
        ],
      });
      toaster.success(
        checked ? 'Call recording enabled' : 'Call recording disabled',
        { theme: Theme.New },
      );
    } catch (error) {
      // Revert on error
      setIsEnabled(!checked);
      toaster.error('Failed to set call recording', { theme: Theme.New });
      // Error handling - API errors are handled by the API layer
    }
  };

  return (
    <div className="feature-badge-chip-wrapper">
      <Switch
        checked={isEnabled}
        onChange={handleToggle}
        size={SwitchSize.Small}
      />
      <OverlayTooltip text="Configure">
        <div
          className="feature-settings-icon"
          {...accessibleOnClick(() => {
            // TODO: Handle call recording settings
            history.push(
              `/dialer/phone-numbers/${row.id}/settings#call-recording`,
            );
          })}
        >
          <PhoneNumberSettingsIcon width={16} height={16} />
        </div>
      </OverlayTooltip>
    </div>
  );
};

// Voicemail Greeting Cell Component
const VoicemailGreetingCell: React.FC<{ row: PhoneNumber }> = ({ row }) => {
  const [isEnabled, setIsEnabled] = useState(!!row.voicemailGreeting);
  const phoneNumberId = row.id;
  const history = useHistory();
  useEffect(() => {
    // keep UI in sync when fresh data arrives
    setIsEnabled(!!row.voicemailGreeting);
  }, [row.voicemailGreeting]);

  const handleToggle = async (checked: boolean) => {
    // Optimistic update
    setIsEnabled(checked);
    try {
      await updatePhoneNumberSettings({
        phoneNumberIds: [phoneNumberId],
        settings: [
          {
            code: PHONE_NUMBER_SETTING_CODES.VOICEMAIL_ENABLED,
            value: checked ? 'true' : 'false',
          },
        ],
      });
      toaster.success(
        checked ? 'Voicemail greeting enabled' : 'Voicemail greeting disabled',
        { theme: Theme.New },
      );
    } catch (error) {
      // Revert on error
      setIsEnabled(!checked);
      toaster.error('Failed to set voicemail greeting', { theme: Theme.New });
      // Error handling - API errors are handled by the API layer
    }
  };

  return (
    <div className="feature-badge-chip-wrapper">
      <Switch
        checked={isEnabled}
        onChange={handleToggle}
        size={SwitchSize.Small}
      />
      <OverlayTooltip text="Configure">
        <div
          className="feature-settings-icon"
          {...accessibleOnClick(() => {
            // TODO: Handle voicemail greeting settings
            history.push(
              `/dialer/phone-numbers/${row.id}/settings#voicemail-greeting`,
            );
          })}
        >
          <PhoneNumberSettingsIcon width={16} height={16} />
        </div>
      </OverlayTooltip>
    </div>
  );
};

// Editable Friendly Name Cell Component
const EditableFriendlyNameCell: React.FC<{ row: PhoneNumber }> = ({ row }) => {
  const dispatch = useDispatch();
  const { id: phoneNumberId, friendlyName } = row;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(friendlyName || '');
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const [editableFieldPosition, setEditableFieldPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const [columnWidth, setColumnWidth] = useState(0);

  // Optimistic state for immediate UI update
  const [optimisticValue, setOptimisticValue] = useState<string | null>(null);

  // Get the current display value - use optimistic value if available, otherwise use actual friendlyName
  const currentDisplayValue = useMemo(
    () => (optimisticValue !== null ? optimisticValue : friendlyName || ''),
    [optimisticValue, friendlyName],
  );

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Reset optimistic value when friendlyName changes from API
  // This syncs the UI when the API response updates the row data
  useEffect(() => {
    if (optimisticValue !== null) {
      const actualValue = friendlyName || '';
      // Clear optimistic value when API response matches our optimistic update
      if (actualValue === optimisticValue) {
        setOptimisticValue(null);
      }
    }
  }, [friendlyName, optimisticValue]);

  const getCellPositionAndShowEditableField = useCallback(() => {
    if (cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      setEditableFieldPosition({ top: rect.top, left: rect.left });
      setColumnWidth(rect.width);
    }
    setIsEditing(true);
  }, []);

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Use current display value (optimistic or actual) when opening input
      setEditValue(currentDisplayValue);
      setHasError(false);
      getCellPositionAndShowEditableField();
    },
    [currentDisplayValue, getCellPositionAndShowEditableField],
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Use current display value (optimistic or actual) when opening input
      setEditValue(currentDisplayValue);
      setHasError(false);
      getCellPositionAndShowEditableField();
    },
    [currentDisplayValue, getCellPositionAndShowEditableField],
  );

  const handleChange = useCallback((value?: string) => {
    const newValue = value || '';
    setEditValue(newValue);
    const trimmedValue = newValue.trim();

    // Validate: min 2, max 30 characters (not empty)
    if (
      trimmedValue.length === 0 ||
      trimmedValue.length < 2 ||
      trimmedValue.length > 30
    ) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  }, []);

  const handleSave = useCallback(() => {
    const trimmedValue = editValue.trim();

    // Validate before saving - must be between 2 and 30 characters (not empty)
    if (
      trimmedValue.length === 0 ||
      trimmedValue.length < 2 ||
      trimmedValue.length > 30
    ) {
      toaster.error('Label must be between 2 and 30 characters', {
        theme: Theme.New,
      });
      setHasError(true);
      setIsEditing(false);
      return;
    }

    // Only save if value changed from current display value
    if (trimmedValue !== currentDisplayValue) {
      // Update UI immediately (optimistic update)
      setOptimisticValue(trimmedValue);
      setIsEditing(false);
      setHasError(false);

      // Dispatch API call in background (fire and forget)
      dispatch(
        setPhoneNumberLabelRequest({
          phoneNumberIds: [phoneNumberId],
          label: trimmedValue,
        }),
      );
    } else {
      setIsEditing(false);
      setHasError(false);
    }
  }, [editValue, currentDisplayValue, phoneNumberId, dispatch]);

  const handleBlur = useCallback(() => {
    handleSave();
  }, [handleSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Revert to current display value (optimistic or actual)
        setEditValue(currentDisplayValue);
        setIsEditing(false);
        setHasError(false);
      }
    },
    [handleSave, currentDisplayValue],
  );

  const handleKeyDownDiv = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleEdit(e as any);
      }
    },
    [handleEdit],
  );

  const errorClass = hasError ? ' error' : '';

  const renderEditInput = () => (
    <div
      style={{
        minWidth: `${columnWidth}px`,
      }}
    >
      <Input
        ref={inputRef as React.Ref<HTMLInputElement>}
        type="text"
        value={editValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        autoFocus
        className={`editable-input${errorClass}`}
      />
    </div>
  );

  if (isEditing) {
    const portalClass = hasError ? 'editable-portal error' : 'editable-portal';
    return (
      <>
        <div
          ref={cellRef}
          className="editable-cell"
          onDoubleClick={handleDoubleClick}
          onKeyDown={handleKeyDownDiv}
          role="button"
          tabIndex={0}
        >
          <span className="edit-text">{currentDisplayValue || '-'}</span>
          <Button className="edit-button" onClick={handleEdit}>
            <Pencil width={14} height={14} color="#6B7280" />
          </Button>
        </div>
        {createPortal(
          <div
            className={portalClass}
            style={{
              position: 'absolute',
              zIndex: 9999,
              top: editableFieldPosition.top - 11,
              left: editableFieldPosition.left - 16,
            }}
          >
            {renderEditInput()}
          </div>,
          document.body,
        )}
      </>
    );
  }

  const displayText = currentDisplayValue || '-';

  return (
    <div
      ref={cellRef}
      className="editable-cell"
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDownDiv}
      role="button"
      tabIndex={0}
    >
      <span className="edit-text">
        <TruncatedTextWithAutoTooltip text={currentDisplayValue} />
      </span>
      <Button className="edit-button" onClick={handleEdit}>
        <Pencil width={14} height={14} color="#6B7280" />
      </Button>
    </div>
  );
};

// Assigned User Cell Component
const AssignedUserCell: React.FC<{ row: PhoneNumber }> = ({ row }) => {
  const dispatch = useDispatch();
  const { id: phoneNumberId, assignedUser } = row;

  const teamMembersList = useSelector(
    (state: RootState) => state?.dialer?.teamMembersList || [],
  );

  // Optimistic state for immediate UI update
  const [optimisticSelectedKey, setOptimisticSelectedKey] = useState<
    number | null
  >(null);
  // Track dropdown open state
  const [isOpen, setIsOpen] = useState(false);
  // Track placement (top or bottom) based on viewport space
  const [placement, setPlacement] = useState<Placement>(Placement.Bottom);
  // Ref to the SelectBar element to add/remove class
  const selectBarRef = useRef<HTMLDivElement>(null);

  // Map team members to options for Select component
  const teamMemberOptions = useMemo(() => {
    const optionsArray: Array<{ key: string; value: string }> = [];
    const addedIds = new Set<number>();

    // Add all team members from the list
    if (teamMembersList && teamMembersList.length > 0) {
      teamMembersList.forEach((member) => {
        optionsArray.push({
          key: String(member.id),
          value: member?.name || member?.email || '',
        });
        addedIds.add(member.id);
      });
    }

    // Always include the assigned user if they exist and not already added
    // This ensures the selected option is always available for this specific row
    if (assignedUser && !addedIds.has(assignedUser.id)) {
      const fullName = `${assignedUser.firstName || ''} ${
        assignedUser.lastName || ''
      }`.trim();
      const assignedUserName = fullName || assignedUser.email || '';
      if (assignedUserName) {
        optionsArray.push({
          key: String(assignedUser.id),
          value: assignedUserName,
        });
        addedIds.add(assignedUser.id);
      }
    }

    return optionsArray;
  }, [teamMembersList, assignedUser]);

  // Get the current selected option key based on assigned user
  // Use optimistic value if available, otherwise use actual assigned user
  const selectedOptionKey = useMemo(() => {
    if (optimisticSelectedKey !== null) {
      return String(optimisticSelectedKey);
    }
    if (!assignedUser) return null;
    return String(assignedUser.id);
  }, [assignedUser, optimisticSelectedKey]);

  // Reset optimistic value when assigned user changes from API
  // This syncs the UI when the API response updates the row data
  useEffect(() => {
    if (optimisticSelectedKey !== null) {
      if (assignedUser) {
        // Clear optimistic value when API response matches our optimistic update
        if (Number(assignedUser.id) === optimisticSelectedKey) {
          setOptimisticSelectedKey(null);
        }
      } else {
        // If assignedUser becomes null and we had an optimistic value, clear it
        // (This handles edge cases like unassignment or errors)
        setOptimisticSelectedKey(null);
      }
    }
  }, [assignedUser, optimisticSelectedKey]);

  // Calculate placement (top or bottom) based on available viewport space
  useEffect(() => {
    if (isOpen && selectBarRef.current) {
      const rect = selectBarRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const estimatedDropdownHeight = 300; // Approximate height of dropdown with search

      // Use bottom if there's enough space, otherwise use top
      if (spaceBelow >= estimatedDropdownHeight || spaceBelow >= spaceAbove) {
        setPlacement(Placement.Bottom);
      } else {
        setPlacement(Placement.Top);
      }
    }
  }, [isOpen]);

  // Add/remove bs-select-open class on SelectBar when dropdown opens/closes
  useEffect(() => {
    if (selectBarRef.current) {
      if (isOpen) {
        selectBarRef.current.classList.add('bs-select-open');
      } else {
        selectBarRef.current.classList.remove('bs-select-open');
      }
    }
  }, [isOpen]);

  // Close dropdown when table scrolls (but not when scrolling inside the dropdown)
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      // Don't close if scrolling inside the dropdown overlay
      if (
        target?.closest('.transfer-overlay') ||
        target?.closest('.transfer-container') ||
        target?.closest('.transfer-content')
      ) {
        return;
      }
      setIsOpen(false);
    };

    // Find the table scroll container
    const tableContainer =
      selectBarRef.current?.closest('.tanstack-table--wrapper') ||
      selectBarRef.current?.closest('.phone-number-list-table');

    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });
    }

    window.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [isOpen]);

  // Handle selection change - update optimistically and dispatch API call in background
  const handleChange = useCallback(
    ([option]: Array<{ key: string; value: string }>) => {
      if (option && option.key) {
        const userId = Number(option.key);

        // Only make API call if the selected user is different from the currently assigned user
        const isDifferentUser = !assignedUser || assignedUser.id !== userId;

        if (isDifferentUser) {
          // Find the user details from teamMembersList
          const selectedUser = teamMembersList.find(
            (member) => member.id === userId,
          );

          // Update UI immediately (optimistic update)
          setOptimisticSelectedKey(userId);

          // Dispatch API call in background (fire and forget) with user details
          dispatch(
            assignPhoneNumberRequest({
              phoneNumberIds: [phoneNumberId],
              userId,
              assignedUser: selectedUser
                ? {
                    id: selectedUser.id,
                    firstName: selectedUser.name || '',
                    lastName: '',
                    email: selectedUser.email || '',
                  }
                : undefined,
            }),
          );

          // Dispatch custom event to notify other components about the assignment change
          window.dispatchEvent(
            new CustomEvent('phoneNumberAssignmentChanged', {
              detail: { phoneNumberId, userId },
            }),
          );
        }

        // Close dropdown after selection
        setIsOpen(false);
      }
    },
    [dispatch, phoneNumberId, assignedUser, teamMembersList],
  );

  const canAssign = hasPermission(Permissions.PHONE_NUMBER_ASSIGNMENT_UPDATE);

  // Show placeholder if no team members available
  if (teamMemberOptions.length === 0) {
    return <span>-</span>;
  }

  // Show read-only text when user lacks assignment permission
  if (!canAssign) {
    const currentOption = teamMemberOptions.find(
      (opt) => opt.key === selectedOptionKey,
    );
    return <span>{currentOption?.value || '-'}</span>;
  }

  return (
    <Select<{ key: string; value: string }>
      options={teamMemberOptions}
      selectedOptionKey={selectedOptionKey}
      selectedOptionRenderer={([option]) => <span>{option?.value || '-'}</span>}
      optionRenderer={(option) => <span>{option?.value}</span>}
      onChange={handleChange}
      placeholder="-"
      showSearch
      searchPlaceholder="Search team member"
      optionFilterProp="value"
      show={isOpen}
      onToggle={setIsOpen}
      selectBarRef={selectBarRef}
      placement={placement}
    />
  );
};

// Re-export for backward compatibility
export type { PhoneNumber };

const columnHelper = createColumnHelper<PhoneNumber>();

export interface ColumnDefinitionCallbacks {
  // Reserved for future use if needed
}

// Actions cell component with dropdown
const PhoneNumberActionsCell: React.FC<{ row: PhoneNumber }> = ({ row }) => {
  const [show, setShow] = useState(false);
  const [showPrimaryModal, setShowPrimaryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );
  const [showRevokeReleaseModal, setShowRevokeReleaseModal] = useState(false);
  const [pendingPrimaryId, setPendingPrimaryId] = useState<number | null>(null);
  const [optimisticAssignedUserId, setOptimisticAssignedUserId] = useState<
    number | null
  >(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const dropdownId = `dropdown-actions-${row.id}`;
  const history = useHistory();
  const dispatch = useDispatch<any>();
  const setPrimaryState = useSelector(
    (state: RootState) => state.dialer.setPrimaryPhoneNumberRequest,
  );
  const deletePhoneNumberRequestStatus = useSelector(
    (state: RootState) => state.dialer.deletePhoneNumberRequest,
  );
  const revokePhoneNumberReleaseRequestStatus = useSelector(
    (state: RootState) => state.dialer.revokePhoneNumberReleaseRequest,
  );
  const isDemoAccount = useSelector(
    (state: RootState) => state.home?.isDemoAccount,
  );
  const loggedInUserId = useSelector((state: RootState) => state.home?.userId);
  const isReleasePending =
    row.status === DialerPhoneNumberStatus.RELEASE_PENDING;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node) &&
        !(event.target as Element)?.closest('.dropdown-menu-custom')
      ) {
        setShow(false);
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show]);

  useEffect(() => {
    executeOnRequestStatus({
      status: setPrimaryState.status,
      onSuccess: () => {
        if (pendingPrimaryId === row.id) {
          toaster.success('Phone number set as primary successfully', {
            theme: Theme.New,
          });
          dispatch(resetSetPrimaryPhoneNumberRequest());
          setShowPrimaryModal(false);
          setShow(false);
          setPendingPrimaryId(null);
        }
      },
      onFailed: () => {
        dispatch(resetSetPrimaryPhoneNumberRequest());
        setShowPrimaryModal(false);
        setShow(false);
        setPendingPrimaryId(null);
      },
    });
  }, [setPrimaryState.status, dispatch, pendingPrimaryId, row.id]);

  // Close modal when delete succeeds (success handler is in parent component)
  useEffect(() => {
    if (deletePhoneNumberRequestStatus.status === RequestStatus.Succeeded) {
      setShowDeleteModal(false);
      setShow(false);
    }
  }, [deletePhoneNumberRequestStatus.status]);

  // Close modal when revoke release succeeds (success handler is in parent component)
  useEffect(() => {
    if (
      revokePhoneNumberReleaseRequestStatus.status === RequestStatus.Succeeded
    ) {
      setShowRevokeReleaseModal(false);
      setShow(false);
    }
  }, [revokePhoneNumberReleaseRequestStatus.status]);

  // Listen to assignment changes from AssignedUserCell
  useEffect(() => {
    const handleAssignmentChange = (
      event: CustomEvent<{ phoneNumberId: number; userId: number }>,
    ) => {
      if (event.detail.phoneNumberId === row.id) {
        setOptimisticAssignedUserId(event.detail.userId);
      }
    };

    window.addEventListener(
      'phoneNumberAssignmentChanged',
      handleAssignmentChange as EventListener,
    );

    return () => {
      window.removeEventListener(
        'phoneNumberAssignmentChanged',
        handleAssignmentChange as EventListener,
      );
    };
  }, [row.id]);

  // Update optimistic assigned user ID when row.assignedUser changes
  useEffect(() => {
    if (row.assignedUser?.id) {
      // Clear optimistic state when actual data matches
      if (optimisticAssignedUserId === row.assignedUser.id) {
        setOptimisticAssignedUserId(null);
      }
    } else {
      // If assignedUser becomes null, clear optimistic state
      setOptimisticAssignedUserId(null);
    }
  }, [row.assignedUser, optimisticAssignedUserId]);

  // Get the effective assigned user ID (optimistic or actual)
  const effectiveAssignedUserId =
    optimisticAssignedUserId ?? row.assignedUser?.id ?? null;

  const handleConfirmPrimary = () => {
    setPendingPrimaryId(row.id);
    dispatch(setPrimaryPhoneNumberRequest({ phoneNumberId: row.id }));
  };

  return (
    <div className="phone-number-actions-cell">
      <Dropdown
        bsPrefix="dropdown-custom"
        drop="down"
        show={show}
        onToggle={setShow}
      >
        <Dropdown.Toggle
          ref={toggleRef}
          bsPrefix="dropdown-toggle-custom"
          variant="dropdown-custom"
          className="phone-number-actions-toggle"
          id={dropdownId}
          as="button"
          type="button"
        >
          <DotsVertical width={16} height={16} />
        </Dropdown.Toggle>
        {typeof document !== 'undefined' &&
          show &&
          createPortal(
            <Dropdown.Menu
              bsPrefix="dropdown-menu-custom"
              show={show}
              className="phone-number-actions-dropdown"
            >
              <Dropdown.Item
                bsPrefix="dropdown-item-custom"
                onClick={() => {
                  history.push(
                    `/dialer/phone-numbers/${row.id}/settings#phone-number-label`,
                  );
                  setShow(false);
                }}
              >
                <div className="gray-txt-15">
                  <PhoneNumberSettingsIcon width={16} height={16} />
                </div>
                <span>Number Settings</span>
              </Dropdown.Item>
              {!row.isPrimaryNumber &&
                effectiveAssignedUserId === loggedInUserId &&
                hasPermission(
                  Permissions.PHONE_NUMBER_MARK_AS_PRIMARY_UPDATE,
                ) && (
                  <Dropdown.Item
                    bsPrefix="dropdown-item-custom"
                    onClick={() => {
                      setShow(false);
                      setShowPrimaryModal(true);
                    }}
                  >
                    <div className="gray-txt-15">
                      <CheckboxIcon width={17} height={17} />
                    </div>
                    <span>Mark as Primary</span>
                  </Dropdown.Item>
                )}
              {hasPermission(Permissions.PHONE_NUMBER_DELETE) &&
                (() => {
                  if (row.isSubscriptionToBeCancelled) {
                    return (
                      <OverlayTooltip
                        text="Can’t revoke this number because its subscription is scheduled for cancellation. To revoke it, go to Billing & Subscriptions."
                        placement={Placement.Bottom}
                      >
                        <Dropdown.Item
                          bsPrefix="dropdown-item-custom"
                          className="dropdown-item-custom--disabled"
                        >
                          <div className="gray-txt-15">
                            <RotateAnticlockwiseDots width={16} height={16} />
                          </div>
                          <span>Revoke Release</span>
                        </Dropdown.Item>
                      </OverlayTooltip>
                    );
                  }

                  if (isReleasePending) {
                    return (
                      <Dropdown.Item
                        bsPrefix="dropdown-item-custom"
                        onClick={() => {
                          setShow(false);
                          setShowRevokeReleaseModal(true);
                        }}
                      >
                        <div className="gray-txt-15">
                          <RotateAnticlockwiseDots width={16} height={16} />
                        </div>
                        <span>Revoke Release</span>
                      </Dropdown.Item>
                    );
                  }

                  return (
                    <Dropdown.Item
                      bsPrefix="dropdown-item-custom"
                      onClick={() => {
                        if (isDemoAccount) {
                          setShowGlobalRestrictionModal(true);
                          return;
                        }
                        setShow(false);
                        setShowDeleteModal(true);
                      }}
                    >
                      <Icon identifier="trash" className="gray-txt-15" />
                      <span>Delete Number</span>
                    </Dropdown.Item>
                  );
                })()}
            </Dropdown.Menu>,
            document.body,
          )}
      </Dropdown>
      <PrimaryNumberConfirmModal
        show={showPrimaryModal}
        onClose={() => setShowPrimaryModal(false)}
        onConfirm={handleConfirmPrimary}
        isSubmitting={setPrimaryState.status === RequestStatus.Pending}
      />
      <DeletePhoneNumberModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        phoneNumber={row}
        onSubmit={() => {
          dispatch(deletePhoneNumberRequest(row.id));
        }}
        isLoading={getIsRequestPending(deletePhoneNumberRequestStatus.status)}
        isFreeNumber={row.isFreeNumber}
      />
      <RevokeReleaseModal
        show={showRevokeReleaseModal}
        onClose={() => setShowRevokeReleaseModal(false)}
        phoneNumber={row}
        onSubmit={() => {
          dispatch(revokePhoneNumberReleaseRequest(row.id));
        }}
        isLoading={getIsRequestPending(
          revokePhoneNumberReleaseRequestStatus.status,
        )}
      />
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Deleting phone number is disabled in demo mode"
        content="This is a demo account with sample data. Signup to delete phone numbers"
        primaryBtnLabel="Sign up"
      />
    </div>
  );
};

const PurchaseDateCell: React.FC<{ value: string }> = ({ value }) => {
  const userTimezone = useSelector((state: RootState) => state?.home?.timeZone);
  if (!value) {
    return <span>-</span>;
  }
  return (
    <OverlayTooltip text={formatNoteTimestamp(value, true, userTimezone)}>
      <span>{formatNoteTimestamp(value, false, userTimezone, true)}</span>
    </OverlayTooltip>
  );
};

export const getPhoneNumberColumnDefinitions = () => [
  columnHelper.accessor('phoneNumber', {
    id: 'phoneNumber',
    header: 'Phone Number',
    size: 340,
    minSize: 340,
    maxSize: 350,
    enableResizing: false,
    cell: ({ getValue, row: { original } }) => {
      const value = getValue();
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const history = useHistory();
      const { isoCountry, isPrimaryNumber, isFreeNumber } = original;
      const formattedNumber = value
        ? formatPhoneNumber(value, isoCountry)
        : '-';
      const flagSrc = isoCountry ? COUNTRY_ISO_FLAG_MAP[isoCountry] : null;
      const hasBothBadges = isPrimaryNumber && isFreeNumber;

      if (!value) {
        return <span className="phone-number-text">-</span>;
      }

      return (
        <div
          className={`phone-number-cell ${
            hasBothBadges ? 'phone-number-cell-has-both-badges' : ''
          }`}
        >
          <div className="phone-content">
            {flagSrc && (
              <ImageIcon
                src={flagSrc}
                alt={`${isoCountry} flag`}
                className="phone-number-flag"
              />
            )}
            <span
              className="phone-number-text"
              {...accessibleOnClick(() => {
                history.push(`/dialer/phone-numbers/${original?.id}/settings`);
              })}
            >
              {formattedNumber}
            </span>
            <CopyButton
              text={formattedNumber}
              copyTooltipText="Copy"
              resetDelay={1000}
              placement={Placement.Bottom}
              className="phone-number-cell__copy-btn"
            />
          </div>
          {isFreeNumber && (
            <OverlayTooltip text="Free Number" placement={Placement.Bottom}>
              <div className="badge-chip free">
                <span>Free</span>
              </div>
            </OverlayTooltip>
          )}
          {isPrimaryNumber && (
            <OverlayTooltip text="Primary Number" placement={Placement.Bottom}>
              <div className="primary-badge-chip">
                <span>Primary</span>
              </div>
            </OverlayTooltip>
          )}
        </div>
      );
    },
  }),

  columnHelper.accessor(
    (row) => {
      const location = [row.locality, row.region].filter(Boolean).join(', ');
      return location || row.isoCountry || '-';
    },
    {
      id: 'location',
      header: 'Region',
      size: 190,
      cell: ({ getValue }) => {
        const value = getValue();
        return <span className="location-text">{value}</span>;
      },
    },
  ),

  columnHelper.accessor('friendlyName', {
    id: 'friendlyName',
    header: 'Phone Number Label',
    size: 190,
    cell: ({ row }) => <EditableFriendlyNameCell row={row.original} />,
  }),

  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Purchase Date',
    size: 210,
    cell: ({ getValue }) => <PurchaseDateCell value={getValue()} />,
  }),

  columnHelper.accessor('assignedUser', {
    id: 'assignedUser',
    header: 'Assigned To',
    size: 244,
    cell: ({ row }) => <AssignedUserCell row={row.original} />,
  }),

  columnHelper.accessor('callForwarding', {
    id: 'callForwarding',
    header: 'Call Forwarding',
    size: 152,
    cell: ({ row }) => <CallForwardingCell row={row.original} />,
  }),

  columnHelper.accessor('callRecording', {
    id: 'callRecording',
    header: 'Call Recording',
    size: 136,
    cell: ({ row }) => <CallRecordingCell row={row.original} />,
  }),

  columnHelper.accessor('voicemailGreeting', {
    id: 'voicemailGreeting',
    header: 'Voicemail Greeting',
    size: 146,
    cell: ({ row }) => <VoicemailGreetingCell row={row.original} />,
  }),

  columnHelper.accessor('numberType', {
    id: 'numberType',
    header: 'Number Type',
    size: 128,
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) {
        return <span>-</span>;
      }
      const numberType = value.charAt(0).toUpperCase() + value.slice(1);
      return (
        <div className="number-type-badge">
          <div className="number-type-badge-content">
            {numberType === 'Mobile' ? (
              <Mobile width={14} height={14} />
            ) : (
              <Phone width={14} height={14} />
            )}
            <span>{numberType}</span>
          </div>
        </div>
      );
    },
  }),

  columnHelper.display({
    id: 'actions',
    header: '',
    size: 64,
    minSize: 64,
    maxSize: 64,
    enablePinning: true,
    enableResizing: false,
    cell: ({ row }) => <PhoneNumberActionsCell row={row.original} />,
  }),
];
